/* eslint-disable @typescript-eslint/naming-convention */
import { PaymentMethod } from '@stripe/stripe-js';
import { Observable } from 'rxjs';

export interface InitiateSetupIntentResponse {
  client_secret: string;
  stripeKey: string;
}

export interface PaymentMethodResponse {
  paymentMethodSet: boolean;
  paymentMethod: string;
  taxId?: string;
  card: {
    last4: string;
    exp_month: number;
    exp_year: number;
  };
  billing_details: {
    address: {
      city: string;
      country: string;
      line1: string;
      line2: string;
      postal_code: string;
      state: string;
    };
    email: string;
    name: string;
    phone: string;
  };
}

export interface BillingEstimation {
  billingAmount: number;
  activeUsers: number;
  activeModules: Array<string>;
}

export interface BillingCustomerInformation {
  hasTrial: boolean;
  trialEnd: number;
  hasPaymentMethod: boolean;
  lastInvoiceDate: number;
  upcomingInvoiceDate: number;
  discount: {
    amount_off: number;
    duration: string;
    duration_in_months: number;
    name: string;
    percent_off: number;
  };
  isPrepaidCustomer: boolean;
  // includes metadata from Stripe
  availableUsers?: { [moduleId: string]: number };
  subscriptions: BillingCustomerSubsciptions;
}

export interface BillingCustomerSubsciptions {
  [subscriptionId: string]: {
    enabledFrom: string;
    enabledUntil: string;
    maxQuantity: number;
    usedQuantity: number;
  };
}

export interface BillingInvoiceInterface {
  invoiceName: string;
  invoiceEmailAddress: string;
  taxId?: string;
  address: {
    state: string;
    country: string;
    city: string;
    line1: string;
    line2?: string;
    postal_code?: string;
  };
}

export abstract class BillingService {
  /**
   *
   * @param tenantUri uri of tenant for settingup setup intent
   *
   * Sets up Setup Intent
   */

  public abstract initiateSetupIntent(tenantUri: string): Observable<InitiateSetupIntentResponse>;

  /**
   * @deprecated This method will be deprecated in future, use newSetPaymentMethod
   * @param tenantUri uri of tenant for setting payment method
   * @param paymentMethodId id of the payment method
   * @param taxId tax Id
   *
   * Sets payment method for tenant
   */
  public abstract setPaymentMethod(
    tenantUri: string,
    paymentMethodId: string | PaymentMethod,
    invoiceEmailAddress: string,
    taxId: string,
    setupIntentId: string
  ): Observable<PaymentMethodResponse>;

  /**
   *
   * @param tenantUri uri of tenant for setting payment method
   * @param paymentMethodId id of the payment method
   * @param taxId tax Id
   *
   * Sets payment method for tenant
   */
  public abstract newSetPaymentMethod(tenantUri: string, paymentMethodId: string, setupIntentId: string): Observable<PaymentMethodResponse>;

  /**
   * Submit billing form
   *
   * @param tenantUri uri of tenant for setting payment method
   * @param billingInvoice billing form value
   */
  public abstract setBillingInvoiceMethod(tenantUri: string, billingInvoice: BillingInvoiceInterface): Observable<BillingInvoiceInterface>;

  /**
   *
   * @param tenantUri uri of tenant whose payment method is to be retrieved
   *
   * Fetches payment method for tenant
   */
  public abstract getPaymentMethod(tenantUri: string): Observable<PaymentMethodResponse>;

  /**
   *
   * @param tenantUri uri of tenant whose billing estimation is to be retrieved
   *
   * Fetches billing estimation for tenant
   */
  public abstract getBillingEstimation(tenantUri: string): Observable<BillingEstimation>;

  /**
   *
   * @param activeUsers no of active users in tenant
   * @param activeModules active modules
   *
   * Calculates billing estimation based on Active Users and Active Modules
   */
  public abstract calculateBillingEstimation(activeUsers: number, activeModules: Array<string>): Observable<BillingEstimation>;

  /**
   * Provides customer billing information for the given tenantUri
   *
   * @param tenantUri Tenant uri whose customer details is to be retireived
   */
  public abstract getCustomerInformation(tenantUri: string): Observable<BillingCustomerInformation>;
}
